import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { leaseService } from '../services/leaseService';
import { FileText, Loader2, Building2, User } from 'lucide-react';

const EMPTY_FORM = {
  unitNumber:      '',
  monthlyRent:     '',
  securityDeposit: '',
  leaseStart:      '',
  leaseEnd:        '',
  status:          'Active',
  terms:           '',
};

export default function LeaseSetupModal({
  open,
  onOpenChange,
  tenantId,
  tenantName,
  propertyId,
  propertyName,
  defaultUnitNumber = '',
  onSaved,
}) {
  const [form, setForm]           = useState(EMPTY_FORM);
  const [existingLease, setExistingLease] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState('');

  // Load existing lease for this tenant whenever modal opens
  useEffect(() => {
    if (!open || !tenantId) return;
    setError('');
    setLoading(true);

    leaseService.getAll()
      .then(leases => {
        const match = leases.find(l => {
          const tid = l.tenant?._id || l.tenant;
          return tid?.toString() === tenantId?.toString();
        });
        if (match) {
          setExistingLease(match);
          setForm({
            unitNumber:      match.unitNumber || '',
            monthlyRent:     match.monthlyRent ?? '',
            securityDeposit: match.securityDeposit ?? '',
            leaseStart:      match.leaseStart ? match.leaseStart.split('T')[0] : '',
            leaseEnd:        match.leaseEnd   ? match.leaseEnd.split('T')[0]   : '',
            status:          match.status || 'Active',
            terms:           match.terms || '',
          });
        } else {
          setExistingLease(null);
          setForm({ ...EMPTY_FORM, unitNumber: defaultUnitNumber });
        }
      })
      .catch(() => setError('Failed to load lease data.'))
      .finally(() => setLoading(false));
  }, [open, tenantId, defaultUnitNumber]);

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!form.unitNumber.trim()) return setError('Unit number is required.');
    if (!form.monthlyRent || Number(form.monthlyRent) <= 0) return setError('Monthly rent must be greater than 0.');
    if (!form.leaseStart) return setError('Lease start date is required.');
    if (!form.leaseEnd)   return setError('Lease end date is required.');
    if (new Date(form.leaseEnd) <= new Date(form.leaseStart)) return setError('Lease end must be after lease start.');

    setSubmitting(true);
    try {
      const payload = {
        tenant:          tenantId,
        property:        propertyId,
        unitNumber:      form.unitNumber.trim(),
        monthlyRent:     Number(form.monthlyRent),
        securityDeposit: form.securityDeposit ? Number(form.securityDeposit) : 0,
        leaseStart:      form.leaseStart,
        leaseEnd:        form.leaseEnd,
        status:          form.status,
        terms:           form.terms,
      };

      const saved = existingLease
        ? await leaseService.update(existingLease._id, payload)
        : await leaseService.create(payload);

      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save lease. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const isEdit = !!existingLease;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-cyan-600" />
            {isEdit ? 'Edit Lease' : 'Set Up Lease'}
          </DialogTitle>
        </DialogHeader>

        {/* Context chips */}
        <div className="flex flex-wrap gap-2 pb-2">
          {tenantName && (
            <span className="inline-flex items-center gap-1.5 text-xs bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full">
              <User className="h-3 w-3" /> {tenantName}
            </span>
          )}
          {propertyName && (
            <span className="inline-flex items-center gap-1.5 text-xs bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full">
              <Building2 className="h-3 w-3" /> {propertyName}
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 pt-1">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {/* Unit + Rent side by side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="unitNumber">Unit Number *</Label>
                <Input
                  id="unitNumber"
                  name="unitNumber"
                  placeholder="e.g. 2B"
                  value={form.unitNumber}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="monthlyRent">Monthly Rent ($) *</Label>
                <Input
                  id="monthlyRent"
                  name="monthlyRent"
                  type="number"
                  min="1"
                  step="0.01"
                  placeholder="1500.00"
                  value={form.monthlyRent}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* Security deposit */}
            <div className="space-y-1.5">
              <Label htmlFor="securityDeposit">Security Deposit ($)</Label>
              <Input
                id="securityDeposit"
                name="securityDeposit"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={form.securityDeposit}
                onChange={handleChange}
              />
            </div>

            {/* Dates side by side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="leaseStart">Lease Start *</Label>
                <Input
                  id="leaseStart"
                  name="leaseStart"
                  type="date"
                  value={form.leaseStart}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="leaseEnd">Lease End *</Label>
                <Input
                  id="leaseEnd"
                  name="leaseEnd"
                  type="date"
                  value={form.leaseEnd}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* Status */}
            <div className="space-y-1.5">
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                name="status"
                value={form.status}
                onChange={handleChange}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="Active">Active</option>
                <option value="Pending">Pending</option>
                <option value="Expired">Expired</option>
                <option value="Terminated">Terminated</option>
              </select>
            </div>

            {/* Terms */}
            <div className="space-y-1.5">
              <Label htmlFor="terms">Lease Terms</Label>
              <Textarea
                id="terms"
                name="terms"
                rows={4}
                placeholder="Enter any specific lease terms, conditions, or notes…"
                value={form.terms}
                onChange={handleChange}
                className="resize-none"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
              >
                {submitting
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{isEdit ? 'Saving…' : 'Creating…'}</>
                  : isEdit ? 'Save Changes' : 'Create Lease'
                }
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
